/**
 * Pure helpers for the master catalog barcode lookup (JD-422).
 *
 * GET /products/master-lookup/:barcode answers one of
 *   { status: "own", productId }
 *   { status: "master", name, ozSize, image, manufacturer: {name, localId}|null, brand: {name, localId}|null }
 *   { status: "none" }
 * A manufacturer or brand with `localId: null` does not exist in the caller's
 * organization yet; the form keeps its name as "pending" and the create
 * endpoint creates it (manufacturerName / brandName in the payload).
 */

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function pendingName(value) {
  const text = String(value ?? "").trim();
  return text ? text : null;
}

/** True when the form has neither a manufacturer id nor a pending name. */
export function needsManufacturer(form) {
  return !hasValue(form.manufacturerId) && !pendingName(form.manufacturerName);
}

/** True when the form has neither a brand id nor a pending name. */
export function needsBrand(form) {
  return !hasValue(form.brandId) && !pendingName(form.brandName);
}

/**
 * Picture URL on a master catalog hit, or null when that product has none
 * (or the lookup was not a master hit).
 */
export function catalogImageUrl(lookup) {
  if (!lookup || lookup.status !== "master") return null;
  const image = String(lookup.image ?? "").trim();
  return image || null;
}

/**
 * Fill the empty form fields from a master catalog hit. Anything the user
 * already typed or picked is kept. Returns the same object when nothing
 * applies, so callers can skip a state update.
 */
export function applyMasterLookup(form, lookup, { manufacturers = [], brands = [] }) {
  if (!lookup || lookup.status !== "master") return form;

  const next = { ...form };
  let changed = false;

  if (!String(form.name ?? "").trim() && pendingName(lookup.name)) {
    next.name = pendingName(lookup.name);
    changed = true;
  }

  if (!hasValue(form.ozSize) && lookup.ozSize !== null && lookup.ozSize !== undefined) {
    next.ozSize = lookup.ozSize;
    changed = true;
  }

  // Manufacturer: only when the form has none. Brand: only when the form has
  // none AND it sits under the manufacturer the form ends up with, otherwise
  // the two Autocompletes would disagree.
  const manufacturer = lookup.manufacturer;
  let manufacturerTakenAsPending = false;
  if (needsManufacturer(form) && manufacturer && pendingName(manufacturer.name)) {
    const localId = manufacturer.localId ?? null;
    const known = manufacturers.some((row) => row.manufacturer_id === localId);
    next.manufacturerId = known ? localId : null;
    next.manufacturerName = known ? null : pendingName(manufacturer.name);
    manufacturerTakenAsPending = !known;
    changed = true;
  }

  const brand = lookup.brand;
  if (needsBrand(form) && brand && pendingName(brand.name)) {
    const localId = brand.localId ?? null;
    const localBrand = brands.find((row) => row.brand_id === localId) || null;
    if (localBrand) {
      if (hasValue(next.manufacturerId) && localBrand.manufacturer_id === next.manufacturerId) {
        next.brandId = localBrand.brand_id;
        next.brandName = null;
        changed = true;
      }
    } else if (
      // A brand that will be created needs the manufacturer the master gave it.
      manufacturer &&
      (next.manufacturerName === pendingName(manufacturer.name) ||
        (hasValue(next.manufacturerId) && next.manufacturerId === (manufacturer.localId ?? null)))
    ) {
      next.brandId = null;
      next.brandName = pendingName(brand.name);
      changed = true;
    }
  }

  // A "(new)" manufacturer without a "(new)" brand would leave the Brand
  // field with nothing to pick (the org has no brands under it yet), so it is
  // only kept together with its brand.
  if (manufacturerTakenAsPending && !next.brandName) {
    next.manufacturerId = form.manufacturerId;
    next.manufacturerName = form.manufacturerName;
    changed =
      next.name !== form.name ||
      next.ozSize !== form.ozSize ||
      next.brandId !== form.brandId;
  }

  return changed ? next : form;
}

/** The manufacturer/brand part of the create payload: ids, or pending names when an id is missing. */
export function productRefsPayload(form) {
  const payload = {
    manufacturerId: hasValue(form.manufacturerId) ? form.manufacturerId : null,
    brandId: hasValue(form.brandId) ? form.brandId : null,
  };
  if (payload.manufacturerId === null && pendingName(form.manufacturerName)) {
    payload.manufacturerName = pendingName(form.manufacturerName);
  }
  if (payload.brandId === null && pendingName(form.brandName)) {
    payload.brandName = pendingName(form.brandName);
  }
  return payload;
}
